import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PayPalHostedButton } from "./PayPalHostedButton";

const LegalLink = ({ slug, children }) => (
  <Link
    to={`/trust-center/${slug}`}
    target="_blank"
    rel="noopener noreferrer"
    className="text-[#22D3EE] hover:underline underline-offset-2"
  >
    {children}
  </Link>
);

const ConsentCheckbox = ({ id, checked, onChange, dim = false, children }) => (
  <label className="flex items-start gap-3 cursor-pointer group" data-testid={`consent-${id}-label`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={`consent-${id}-checkbox`}
      className="mt-0.5 h-4 w-4 shrink-0 rounded border-white/25 bg-white/5 accent-[#22D3EE] cursor-pointer"
    />
    <span className={`text-[11px] leading-relaxed ${dim ? "text-[#7C8FA8]" : "text-[#9AAEC6]"}`}>
      {children}
    </span>
  </label>
);

const NEXT_STEPS = [
  "Complete your secure one-time payment",
  "Create your member login on the next screen",
  "Get instant access to your member dashboard",
  "Start Week 1 of your transformation today",
];

const FEATURES = [
  "Full Body Support wearable shipped to your door",
  "Lifetime access to the 4-Week Web App",
  "Daily routines, pain tracking & weekly reports",
];

/**
 * Polished, premium checkout card that wraps the PayPal Hosted Button.
 * Rendered in multiple sections of the landing page — each instance mounts its
 * own hosted button container.
 */
export const CheckoutCard = ({ testidSuffix = "" }) => {
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeEducational, setAgreeEducational] = useState(false);
  const [optInEmails, setOptInEmails] = useState(false);
  const canCheckout = agreeTerms && agreeEducational;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6 }}
      className="bps-card bps-card-accent w-full max-w-md mx-auto p-7 sm:p-8"
      data-testid={`checkout-card${testidSuffix ? "-" + testidSuffix : ""}`}
    >
      <div className="bps-eyebrow text-[#22D3EE]">Complete System · One-Time Payment</div>
      <h3 className="font-display text-2xl font-semibold text-white mt-2 leading-tight">
        Back Pain Transformer
        <span className="block text-[#9AAEC6] text-base font-normal mt-1">
          + 4-Week Freedom From Back Pain Course
        </span>
      </h3>

      <div className="flex items-end gap-3 mt-5">
        <span className="font-display text-5xl font-bold text-white">$497</span>
        <div className="pb-1.5">
          <div className="text-sm text-[#7C8FA8] line-through">$649 value</div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#22D3EE]">one-time · lifetime access</div>
        </div>
      </div>

      <ul className="mt-5 space-y-2.5">
        {FEATURES.map((f) => (
          <li key={f} className="flex items-start gap-2.5 text-sm text-[#C7D6EA]">
            <svg className="w-4 h-4 mt-0.5 shrink-0 text-[#22D3EE]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {f}
          </li>
        ))}
      </ul>

      <div className="my-6 h-px bg-white/10" />

      {/* Consent checkboxes */}
      <div className="space-y-3 mb-5" data-testid="checkout-consent-block">
        <ConsentCheckbox id="terms" checked={agreeTerms} onChange={setAgreeTerms}>
          I have read and agree to the{" "}
          <LegalLink slug="terms-of-use">Terms of Use</LegalLink>,{" "}
          <LegalLink slug="privacy-policy">Privacy Policy</LegalLink>,{" "}
          <LegalLink slug="medical-disclaimer">Medical Disclaimer</LegalLink>,{" "}
          <LegalLink slug="participant-agreement">Participant Agreement &amp; Safety Acknowledgement</LegalLink>, and{" "}
          <LegalLink slug="freedom-promise">Freedom Promise™</LegalLink>.{" "}
          <span className="text-[#F87171]">*</span>
        </ConsentCheckbox>

        <ConsentCheckbox id="educational" checked={agreeEducational} onChange={setAgreeEducational}>
          I understand that the Freedom From Back Pain™ Program is an educational wellness program
          designed to support healthy movement habits — it is not medical treatment, diagnosis, or a
          substitute for professional medical advice. I am participating voluntarily and accept
          responsibility for exercising within my own physical limits.{" "}
          <span className="text-[#F87171]">*</span>
        </ConsentCheckbox>

        <ConsentCheckbox id="emails" checked={optInEmails} onChange={setOptInEmails} dim>
          Yes, I'd like to receive educational emails, program tips, and occasional offers. I can
          unsubscribe at any time. (Optional)
        </ConsentCheckbox>
      </div>

      {/* PayPal Hosted Button — locked until mandatory consents are checked */}
      <div className="relative" data-testid="checkout-paypal-wrapper">
        <div className={canCheckout ? "" : "opacity-40 pointer-events-none select-none"} aria-hidden={!canCheckout}>
          <PayPalHostedButton />
        </div>
        {!canCheckout && (
          <div
            className="mt-2 flex items-center justify-center gap-2 text-[11px] text-[#F0A868]"
            data-testid="consent-hint"
          >
            <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
            Please accept the required agreements above to continue
          </div>
        )}
      </div>

      <div className="mt-4 text-center">
        <Link
          to="/thank-you"
          className="text-xs text-[#9AAEC6] hover:text-[#22D3EE] transition-colors underline-offset-4 hover:underline"
          data-testid="claim-access-link"
        >
          Already completed payment? Claim your access →
        </Link>
      </div>

      {/* Trust / guarantee row */}
      <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] text-[#7C8FA8]">
        <span className="inline-flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-[#10B981]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/></svg>
          30-Day Money-Back Guarantee
        </span>
        <span className="inline-flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-[#22D3EE]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          256-bit Secure Checkout
        </span>
      </div>

      <div className="mt-3 text-center text-[11px] uppercase tracking-[0.16em] text-[#4F627B]">
        Visa · Mastercard · Amex · PayPal · Venmo
      </div>

      {/* What happens next */}
      <div className="mt-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] p-4">
        <div className="bps-eyebrow mb-2">What happens next</div>
        <ol className="space-y-1.5">
          {NEXT_STEPS.map((s, i) => (
            <li key={s} className="flex items-start gap-2.5 text-xs text-[#B8C7DC]">
              <span className="shrink-0 w-4 h-4 rounded-full bg-[#22D3EE]/15 text-[#22D3EE] text-[10px] font-semibold flex items-center justify-center">
                {i + 1}
              </span>
              {s}
            </li>
          ))}
        </ol>
      </div>
    </motion.div>
  );
};
